// frontend/src/components/Dashboard/SalesChart.jsx - CLEANED
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCurrency } from "../../context/CurrencyContext";
import {
  faChartLine,
  faArrowTrendUp,
  faArrowTrendDown,
  faInfoCircle,
  faGlobe,
  faAward,
} from "@fortawesome/free-solid-svg-icons";

const SalesChart = ({ data, onPeriodChange, title = "Sales Overview" }) => {
  const { formatAmount, currency } = useCurrency();
  const [period, setPeriod] = useState("30d");
  const [hoveredBar, setHoveredBar] = useState(null);

  const chartData =
    data && data.length > 0
      ? data
      : [
          { day: "Mon", sales: 120, global: 80 },
          { day: "Tue", sales: 180, global: 120 },
          { day: "Wed", sales: 150, global: 100 },
          { day: "Thu", sales: 220, global: 160 },
          { day: "Fri", sales: 280, global: 200 },
          { day: "Sat", sales: 200, global: 140 },
          { day: "Sun", sales: 160, global: 110 },
        ];

  const maxValue = Math.max(...chartData.map((d) => d.sales), 1);
  const totalSales = chartData.reduce((sum, d) => sum + d.sales, 0);
  const totalGlobal = chartData.reduce((sum, d) => sum + (d.global || 0), 0);
  const globalPercentage =
    totalSales > 0 ? (totalGlobal / totalSales) * 100 : 0;
  const avgSales = totalSales / chartData.length;
  const trend =
    chartData.length > 1
      ? chartData[chartData.length - 1].sales - chartData[0].sales
      : 0;

  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod);
    if (onPeriodChange) onPeriodChange(newPeriod);
  };

  const formatCurrency = (amount) => formatAmount(amount, true);

  return (
    <div className="sales-chart">
      <div className="chart-header">
        <div className="chart-title-section">
          <FontAwesomeIcon icon={faChartLine} className="chart-icon" />
          <h3>{title}</h3>
          <span className="badge premium">
            <FontAwesomeIcon icon={faAward} /> Premium Quality
          </span>
          <span className="badge currency">{currency}</span>
        </div>
        <div className="chart-controls">
          <div className="chart-stats">
            <span className="chart-stat">
              <strong>Total:</strong> {formatCurrency(totalSales)}
            </span>
            <span className="chart-stat">
              <strong>Avg:</strong> {formatCurrency(avgSales)}
            </span>
            <span className="chart-stat global">
              <FontAwesomeIcon icon={faGlobe} /> {globalPercentage.toFixed(0)}%
              Globally Sourced
            </span>
            <span
              className={`chart-trend ${trend >= 0 ? "positive" : "negative"}`}
            >
              <FontAwesomeIcon
                icon={trend >= 0 ? faArrowTrendUp : faArrowTrendDown}
              />
              {trend >= 0 ? "+" : ""}
              {formatCurrency(trend)}
            </span>
          </div>
          <select
            className="chart-period"
            value={period}
            onChange={handlePeriodChange}
          >
            <option value="7d">Last 7 Days</option>
            <option value="14d">Last 14 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-bars">
          {chartData.map((item, index) => {
            const height = (item.sales / maxValue) * 100;
            const globalHeight = ((item.global || 0) / maxValue) * 100;
            const isHovered = hoveredBar === index;
            const dayLabel = item.day || `Day ${index + 1}`;

            return (
              <div
                key={index}
                className="chart-bar-wrapper"
                onMouseEnter={() => setHoveredBar(index)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div
                  className="chart-bar-container"
                  style={{ height: `${Math.max(height, 5)}%` }}
                >
                  <div
                    className="chart-bar global"
                    style={{ height: `${Math.max(globalHeight, 2)}%` }}
                  />
                  <div
                    className={`chart-bar total ${isHovered ? "hovered" : ""}`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                  >
                    {isHovered && (
                      <div className="chart-bar-tooltip">
                        <div className="tooltip-day">{dayLabel}</div>
                        <div className="tooltip-value">
                          {formatCurrency(item.sales)}
                        </div>
                        {item.global !== undefined && (
                          <div className="tooltip-global">
                            <FontAwesomeIcon icon={faGlobe} />{" "}
                            {formatCurrency(item.global)} Globally Sourced
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="chart-label">{dayLabel}</div>
                <div className="chart-value">{formatCurrency(item.sales)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="chart-footer">
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color total" /> Total Sales
          </span>
          <span className="legend-item">
            <span className="legend-color global" /> Globally Sourced
          </span>
          <span className="legend-item">
            <span className="legend-color currency" /> Displayed in {currency}
          </span>
        </div>
        <div className="chart-info">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>Hover over bars for details</span>
        </div>
      </div>
    </div>
  );
};

export default SalesChart;
